import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Checkbox } from "antd";
import { ApolloError } from "@apollo/client";
import { ArrowRight, ChevronsUpDown, Mail, ShieldCheck } from "lucide-react";

import routeNames from "constants/route-names";
import {
	CLAIM_JURISDICTION_SUPPORT_EMAIL,
	ClaimJurisdictionStep,
	NEW_JURISDICTION_AHJ_TYPE_OPTIONS,
	NEW_JURISDICTION_DEFAULT_STATE,
	TERMS_AND_CONDITIONS_URL,
	US_STATE_NAMES
} from "constants/constants";
import { useAuth } from "context/AuthContext";
import { useGetClaimableJurisdictionsQuery } from "graphql/queries/get-claimable-jurisdictions.generated";
import { useResolveAuthorityClaimJurisdictionLazyQuery } from "graphql/queries/resolve-authority-claim-jurisdiction.generated";
import { useSubmitAuthorityClaimMutation } from "graphql/mutations/submit-authority-claim.generated";
import ClaimJurisdictionLayout from "components/claim-jurisdiction-layout/claim-jurisdiction-layout";
import ClaimJurisdictionCard from "components/claim-jurisdiction-card/claim-jurisdiction-card";
import ClaimJurisdictionButton from "components/claim-jurisdiction-button/claim-jurisdiction-button";
import ClaimJurisdictionPicker from "components/claim-jurisdiction-picker/claim-jurisdiction-picker";
import { formatJurisdictionRegion } from "utils/claim-jurisdiction";

const JURISDICTION_LABEL_ID = "claim-jurisdiction-picker-label";

// The option to add a jurisdiction that is not in the list. The service also checks the
// claims.new_jurisdiction row of feature_flags, so both have to be on.
const featureFlagClaimNewJurisdictionEnabled = process.env.REACT_APP_FF_CLAIM_NEW_JURISDICTION_ENABLED == "true" || false;

type NewJurisdictionValues = {
	name: string;
	ahjType: string;
	city: string;
	county: string;
	state: string;
};

const EMPTY_NEW_JURISDICTION: NewJurisdictionValues = {
	name: "",
	ahjType: "",
	city: "",
	county: "",
	state: NEW_JURISDICTION_DEFAULT_STATE
};

const STATE_OPTIONS = Object.entries(US_STATE_NAMES)
	.map(([code, name]) => ({ code, name }))
	.sort((first, second) => first.name.localeCompare(second.name));

const readServerMessage = (error: unknown, fallbackMessage: string) =>
	(error as ApolloError).graphQLErrors?.[0]?.message || fallbackMessage;

/**
 * /claim: a person from an AHJ claims the Authority organization of their jurisdiction.
 * 1. EMAIL: the work email finds the jurisdiction (known contact or email domain).
 * 2. JURISDICTION: the person confirms or picks the jurisdiction and enters their name. When the
 *    option is on, a jurisdiction that is not in the list can be added instead; an admin
 *    reviews that claim and the jurisdiction is created when the account is created.
 * 3. THANKS: the claim is saved and the email link is on its way. Opening the link goes to
 *    /claim/setup, which creates the account.
 */
const ClaimJurisdiction = () => {
	const navigate = useNavigate();
	const { loggedUser } = useAuth();

	const [step, setStep] = useState(ClaimJurisdictionStep.EMAIL);
	const [email, setEmail] = useState("");
	const [isEmailMatched, setIsEmailMatched] = useState(false);
	const [selectedJurisdictionId, setSelectedJurisdictionId] = useState<string | null>(null);
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [termsAccepted, setTermsAccepted] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [continueMessage, setContinueMessage] = useState("");
	const [canAddJurisdiction, setCanAddJurisdiction] = useState(false);
	const [isAddingJurisdiction, setIsAddingJurisdiction] = useState(false);
	const [newJurisdiction, setNewJurisdiction] = useState<NewJurisdictionValues>(EMPTY_NEW_JURISDICTION);

	const { data: jurisdictionsData } = useGetClaimableJurisdictionsQuery();
	const [resolveJurisdiction, { loading: isResolving }] = useResolveAuthorityClaimJurisdictionLazyQuery({
		fetchPolicy: "network-only"
	});
	// The app client returns GraphQL errors inside the result (errorPolicy "all"). "none" makes the
	// promise reject, so the catch shows the message of the service (for example a jurisdiction
	// that is already in the list) instead of moving to the thanks step.
	const [submitAuthorityClaim, { loading: isSubmitting }] = useSubmitAuthorityClaimMutation({ errorPolicy: "none" });

	const jurisdictions = jurisdictionsData?.jurisdictions ?? [];
	const selectedJurisdiction = jurisdictions.find(jurisdiction => jurisdiction.id === selectedJurisdictionId);

	const handleFindJurisdiction = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setErrorMessage("");

		const trimmedEmail = email.trim();
		const fallbackMessage = "We could not look up your jurisdiction. Please try again.";

		try {
			const { data, error } = await resolveJurisdiction({ variables: { email: trimmedEmail } });
			if (error) {
				setErrorMessage(readServerMessage(error, fallbackMessage));
				return;
			}

			// A match outside the list (for example an inactive jurisdiction) is treated as no match.
			const matchedJurisdictionId = data?.resolve_authority_claim_jurisdiction.jurisdiction_id ?? null;
			const isMatchInList = jurisdictions.some(jurisdiction => jurisdiction.id === matchedJurisdictionId);

			setEmail(trimmedEmail);
			setIsEmailMatched(isMatchInList);
			setSelectedJurisdictionId(isMatchInList ? matchedJurisdictionId : null);
			setCanAddJurisdiction(!!data?.resolve_authority_claim_jurisdiction.can_add_jurisdiction);
			setIsAddingJurisdiction(false);
			setStep(ClaimJurisdictionStep.JURISDICTION);
		} catch (error) {
			setErrorMessage(readServerMessage(error, fallbackMessage));
		}
	};

	const handleClaimJurisdiction = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!isAddingJurisdiction && !selectedJurisdictionId) return;
		setErrorMessage("");

		const proposedJurisdiction = {
			name: newJurisdiction.name.trim(),
			ahj_type: newJurisdiction.ahjType,
			city: newJurisdiction.city.trim() || null,
			county: newJurisdiction.county.trim() || null,
			state: newJurisdiction.state
		};

		try {
			await submitAuthorityClaim({
				variables: {
					email,
					first_name: firstName.trim(),
					last_name: lastName.trim(),
					jurisdiction_id: isAddingJurisdiction ? null : selectedJurisdictionId,
					new_jurisdiction: isAddingJurisdiction ? proposedJurisdiction : null,
					terms_accepted: termsAccepted,
					app_base_url: `${window.location.origin}${process.env.PUBLIC_URL}`
				}
			});
			setStep(ClaimJurisdictionStep.THANKS);
		} catch (error) {
			setErrorMessage(readServerMessage(error, "Your claim could not be saved. Please try again."));
		}
	};

	const handleChangeEmail = () => {
		setErrorMessage("");
		setStep(ClaimJurisdictionStep.EMAIL);
	};

	// The email link signs the person in. When it was opened on this same device, the session is
	// already here and the setup can continue; otherwise the link has to be opened first.
	const handleContinueToSetup = () => {
		const isClaimEmailSignedIn =
			!!loggedUser?.emailVerified && loggedUser?.email?.toLowerCase() === email.toLowerCase();

		if (isClaimEmailSignedIn) {
			navigate(routeNames.CLAIM_JURISDICTION_SETUP);
			return;
		}

		setContinueMessage(`Open the verification link we sent to ${email} to continue. It signs you in and finishes the setup.`);
	};

	const isAddJurisdictionAvailable = featureFlagClaimNewJurisdictionEnabled && canAddJurisdiction;

	const isNewJurisdictionComplete = !!newJurisdiction.name.trim() && !!newJurisdiction.ahjType && !!newJurisdiction.state;
	const isJurisdictionChosen = isAddingJurisdiction ? isNewJurisdictionComplete : !!selectedJurisdictionId;

	const canClaimJurisdiction =
		isJurisdictionChosen && !!firstName.trim() && !!lastName.trim() && termsAccepted && !isSubmitting;

	const claimedJurisdictionName = isAddingJurisdiction ? newJurisdiction.name.trim() : selectedJurisdiction?.name;

	let jurisdictionDescription = isEmailMatched
		? "We found a match for your email domain. Review it below, or choose a different jurisdiction."
		: "We couldn’t find an exact domain match. Search below to select your jurisdiction.";
	if (isAddingJurisdiction) {
		jurisdictionDescription =
			"Tell us about the jurisdiction you represent. Our team will review it before your access is ready.";
	}

	const updateNewJurisdiction = (field: keyof NewJurisdictionValues, value: string) => {
		setNewJurisdiction(values => ({ ...values, [field]: value }));
	};

	const jurisdictionFromListField = (
		<div className="claim-jurisdiction-field">
			<div id={JURISDICTION_LABEL_ID} className="claim-jurisdiction-label">
				Your jurisdiction
			</div>
			<ClaimJurisdictionPicker
				jurisdictions={jurisdictions}
				selectedJurisdictionId={selectedJurisdictionId}
				onSelect={setSelectedJurisdictionId}
				labelId={JURISDICTION_LABEL_ID}
			/>
			{selectedJurisdiction && (
				<div className="claim-jurisdiction-helper">
					{formatJurisdictionRegion(selectedJurisdiction)} · Not right? Open the list to change it.
				</div>
			)}
			{isAddJurisdictionAvailable && (
				<p className="claim-jurisdiction-assistance">
					Not in the list?{" "}
					<button type="button" className="claim-jurisdiction-inline-link" onClick={() => setIsAddingJurisdiction(true)}>
						Add your jurisdiction
					</button>
				</p>
			)}
			<p className="claim-jurisdiction-assistance">
				Need assistance? Email{" "}
				<a href={`mailto:${CLAIM_JURISDICTION_SUPPORT_EMAIL}`} className="claim-jurisdiction-assistance-email">
					{CLAIM_JURISDICTION_SUPPORT_EMAIL}
				</a>{" "}
				and we’ll help you get started.
			</p>
		</div>
	);

	const newJurisdictionFields = (
		<>
			<div className="claim-jurisdiction-field">
				<label htmlFor="claim-jurisdiction-new-name" className="claim-jurisdiction-label">
					Jurisdiction name
				</label>
				<input
					id="claim-jurisdiction-new-name"
					required
					maxLength={200}
					placeholder="e.g. City of Elgin Fire Marshal’s Office"
					value={newJurisdiction.name}
					onChange={event => updateNewJurisdiction("name", event.target.value)}
					className="claim-jurisdiction-input"
				/>
			</div>

			<div className="claim-jurisdiction-field">
				<label htmlFor="claim-jurisdiction-new-type" className="claim-jurisdiction-label">
					Type of authority
				</label>
				<div className="claim-jurisdiction-select">
					<select
						id="claim-jurisdiction-new-type"
						required
						value={newJurisdiction.ahjType}
						onChange={event => updateNewJurisdiction("ahjType", event.target.value)}
						className="claim-jurisdiction-input claim-jurisdiction-select-input"
					>
						<option value="" disabled>
							Select a type
						</option>
						{NEW_JURISDICTION_AHJ_TYPE_OPTIONS.map(option => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
					<ChevronsUpDown size={16} className="claim-jurisdiction-select-icon" />
				</div>
			</div>

			<div className="claim-jurisdiction-name-fields">
				<div className="claim-jurisdiction-field">
					<label htmlFor="claim-jurisdiction-new-city" className="claim-jurisdiction-label">
						City
					</label>
					<input
						id="claim-jurisdiction-new-city"
						maxLength={120}
						value={newJurisdiction.city}
						onChange={event => updateNewJurisdiction("city", event.target.value)}
						className="claim-jurisdiction-input"
					/>
				</div>
				<div className="claim-jurisdiction-field">
					<label htmlFor="claim-jurisdiction-new-county" className="claim-jurisdiction-label">
						County
					</label>
					<input
						id="claim-jurisdiction-new-county"
						maxLength={120}
						value={newJurisdiction.county}
						onChange={event => updateNewJurisdiction("county", event.target.value)}
						className="claim-jurisdiction-input"
					/>
				</div>
			</div>

			<div className="claim-jurisdiction-field">
				<label htmlFor="claim-jurisdiction-new-state" className="claim-jurisdiction-label">
					State
				</label>
				<div className="claim-jurisdiction-select">
					<select
						id="claim-jurisdiction-new-state"
						required
						value={newJurisdiction.state}
						onChange={event => updateNewJurisdiction("state", event.target.value)}
						className="claim-jurisdiction-input claim-jurisdiction-select-input"
					>
						{STATE_OPTIONS.map(option => (
							<option key={option.code} value={option.code}>
								{option.name}
							</option>
						))}
					</select>
					<ChevronsUpDown size={16} className="claim-jurisdiction-select-icon" />
				</div>
				<p className="claim-jurisdiction-assistance">
					Found it in the list after all?{" "}
					<button type="button" className="claim-jurisdiction-inline-link" onClick={() => setIsAddingJurisdiction(false)}>
						Choose from the list
					</button>
				</p>
			</div>
		</>
	);

	const errorAlert = errorMessage ? (
		<Alert message={errorMessage} type="error" showIcon className="claim-jurisdiction-alert" />
	) : null;

	const emailStepCard = (
		<ClaimJurisdictionCard
			key={ClaimJurisdictionStep.EMAIL}
			eyebrowIcon={<ShieldCheck size={16} />}
			eyebrow="STEP 1 OF 2"
			title="Make it official."
			description="Start with your work email so we can help find the jurisdiction you represent."
		>
			<form className="claim-jurisdiction-form" onSubmit={handleFindJurisdiction}>
				<div className="claim-jurisdiction-field">
					<label htmlFor="claim-jurisdiction-email" className="claim-jurisdiction-label">
						Work email
					</label>
					<input
						id="claim-jurisdiction-email"
						type="email"
						autoComplete="email"
						required
						maxLength={254}
						placeholder="you@yourdepartment.gov"
						value={email}
						onChange={event => setEmail(event.target.value)}
						className="claim-jurisdiction-input"
					/>
					<div className="claim-jurisdiction-helper">Use your department or government email address.</div>
				</div>

				{errorAlert}

				<ClaimJurisdictionButton variant="primary" type="submit" disabled={isResolving}>
					Find my jurisdiction
					<ArrowRight size={16} />
				</ClaimJurisdictionButton>

				<div className="claim-jurisdiction-note">
					<ShieldCheck size={16} className="claim-jurisdiction-note-icon" />
					Only authorized representatives should claim a jurisdiction. Email verification is required before access.
				</div>
			</form>
		</ClaimJurisdictionCard>
	);

	const jurisdictionStepCard = (
		<ClaimJurisdictionCard
			key={ClaimJurisdictionStep.JURISDICTION}
			eyebrowIcon={<ShieldCheck size={16} />}
			eyebrow="STEP 2 OF 2"
			title={isAddingJurisdiction ? "Add your jurisdiction" : "Is this your jurisdiction?"}
			description={jurisdictionDescription}
		>
			<form className="claim-jurisdiction-form" onSubmit={handleClaimJurisdiction}>
				<div className="claim-jurisdiction-field">
					<label htmlFor="claim-jurisdiction-email" className="claim-jurisdiction-label">
						Work email
					</label>
					<input id="claim-jurisdiction-email" type="email" readOnly value={email} className="claim-jurisdiction-input" />
				</div>

				{isAddingJurisdiction ? newJurisdictionFields : jurisdictionFromListField}

				<div className="claim-jurisdiction-name-fields">
					<div className="claim-jurisdiction-field">
						<label htmlFor="claim-jurisdiction-first-name" className="claim-jurisdiction-label">
							First name
						</label>
						<input
							id="claim-jurisdiction-first-name"
							autoComplete="given-name"
							required
							value={firstName}
							onChange={event => setFirstName(event.target.value)}
							className="claim-jurisdiction-input"
						/>
					</div>
					<div className="claim-jurisdiction-field">
						<label htmlFor="claim-jurisdiction-last-name" className="claim-jurisdiction-label">
							Last name
						</label>
						<input
							id="claim-jurisdiction-last-name"
							autoComplete="family-name"
							required
							value={lastName}
							onChange={event => setLastName(event.target.value)}
							className="claim-jurisdiction-input"
						/>
					</div>
				</div>

				<Checkbox
					checked={termsAccepted}
					onChange={event => setTermsAccepted(event.target.checked)}
					className="claim-jurisdiction-terms"
				>
					I agree to the&nbsp;
					<a href={TERMS_AND_CONDITIONS_URL} target="_blank" rel="noopener noreferrer">
						Terms and Conditions
					</a>
				</Checkbox>

				{errorAlert}

				<ClaimJurisdictionButton variant="primary" type="submit" disabled={!canClaimJurisdiction}>
					Claim jurisdiction
					<ArrowRight size={16} />
				</ClaimJurisdictionButton>

				<ClaimJurisdictionButton variant="link" onClick={handleChangeEmail}>
					Change email
				</ClaimJurisdictionButton>

				<div className="claim-jurisdiction-note">
					<ShieldCheck size={16} className="claim-jurisdiction-note-icon" />
					By continuing, you confirm that you’re authorized to represent this jurisdiction.
				</div>
			</form>
		</ClaimJurisdictionCard>
	);

	const thanksStepCard = (
		<ClaimJurisdictionCard
			key={ClaimJurisdictionStep.THANKS}
			eyebrowIcon={<Mail size={16} />}
			eyebrow="ONE MORE STEP"
			title="Thanks for claiming your jurisdiction!"
			description="Check your work email for a verification link to complete the process and access your Ollie dashboard."
		>
			<div className="claim-jurisdiction-summary">
				<div className="claim-jurisdiction-summary-email">{email}</div>
				<div className="claim-jurisdiction-summary-jurisdiction">{claimedJurisdictionName}</div>
			</div>

			<p className="claim-jurisdiction-paragraph">
				The verification link confirms your work email before access is granted. If you don’t see it, check your spam
				folder.
			</p>

			{continueMessage && <Alert message={continueMessage} type="info" showIcon className="claim-jurisdiction-alert" />}

			<ClaimJurisdictionButton variant="primary" onClick={handleContinueToSetup}>
				Continue to setup
				<ArrowRight size={16} />
			</ClaimJurisdictionButton>
		</ClaimJurisdictionCard>
	);

	const cardByStep = {
		[ClaimJurisdictionStep.EMAIL]: emailStepCard,
		[ClaimJurisdictionStep.JURISDICTION]: jurisdictionStepCard,
		[ClaimJurisdictionStep.THANKS]: thanksStepCard
	};

	return (
		<ClaimJurisdictionLayout
			title={
				<>
					Your jurisdiction.
					<br />
					Your Ollie.
				</>
			}
		>
			<div className="claim-jurisdiction">{cardByStep[step]}</div>
		</ClaimJurisdictionLayout>
	);
};

export default ClaimJurisdiction;
